/**
 * 패널 플래닝 — String/Module 추가 시 flattened·트리·맵 증분 patch (전체 rebuild 없음)
 */
import { cloneFlattenedSiteDesign, getDataByLevel } from '@/utils/siteDesignDataFlattener.js';
import { findArrayTreeItemBySeq } from './mapHierarchySelectionContext.js';
import {
  moduleFromApi,
  moduleDomainToTreeFields,
  stringFromApi,
  stringFromFlatRecord,
  stringDomainToTreeFields
} from './panelHierarchyMappers.js';
import { appendStringToTree } from './flattenModuleTreeRows.js';

/** Spring Data REST HAL self href → solar array DB seq */
export function extractSolarArraySeqFromHalUri(uri) {
  return extractSolarHierarchySeqFromHalUri(uri, 'solarArrays');
}

/** Spring Data REST HAL self href → solar string DB seq */
export function extractSolarStringSeqFromHalUri(uri) {
  return extractSolarHierarchySeqFromHalUri(uri, 'solarStrings');
}

/** Spring Data REST HAL self href → solar section DB seq */
export function extractSolarSectionSeqFromHalUri(uri) {
  return extractSolarHierarchySeqFromHalUri(uri, 'solarSections');
}

function extractSolarHierarchySeqFromHalUri(uri, collection) {
  if (!uri || typeof uri !== 'string' || !collection) {
    return null;
  }
  const escaped = collection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const patterns = [
    new RegExp(`/${escaped}/(\\d+)(?:/|$|\\?)`, 'i'),
    new RegExp(`(?:^|[/?#])${escaped}/(\\d+)(?:/|$|[?#])`, 'i')
  ];
  for (const pattern of patterns) {
    const match = uri.match(pattern);
    if (match) {
      const n = Number(match[1]);
      if (Number.isFinite(n)) {
        return n;
      }
    }
  }
  return null;
}

/** POST/GET /solarSections 응답 본문·Location·HAL에서 DB seq 추출 */
export function resolveSolarSectionSeqFromApiPayload(data, headers = null) {
  if (data == null && headers == null) {
    return null;
  }
  const fromId = readNumericId(data?.id ?? data?.seq);
  if (fromId != null) {
    return fromId;
  }
  const fromLocation = extractSolarSectionSeqFromHalUri(headers?.location);
  if (fromLocation != null) {
    return fromLocation;
  }
  return extractSolarSectionSeqFromHalUri(data?._links?.self?.href);
}

function readNumericId(value) {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** POST /solarArrays 응답 본문·HAL에서 DB seq 추출 */
export function resolveSolarArraySeqFromApiPayload(raw) {
  if (raw == null) {
    return null;
  }
  const candidates = [raw, unwrapSolarArrayApiPayload(raw)];
  for (const payload of candidates) {
    if (!payload || typeof payload !== 'object') {
      continue;
    }
    const fromId = readNumericId(payload.id ?? payload.seq);
    if (fromId != null) {
      return fromId;
    }
    const href = payload._links?.self?.href;
    if (typeof href === 'string') {
      const fromHref = extractSolarArraySeqFromHalUri(href);
      if (fromHref != null) {
        return fromHref;
      }
    }
  }
  return null;
}

/** API 응답 래핑(data / content / solarArray 등) 제거 */
export function unwrapSolarArrayApiPayload(raw) {
  if (raw == null || typeof raw !== 'object') {
    return raw;
  }
  if (raw.id != null || raw.seq != null) {
    return raw;
  }
  if (raw.content && typeof raw.content === 'object') {
    return unwrapSolarArrayApiPayload(raw.content);
  }
  if (raw.data && typeof raw.data === 'object') {
    return unwrapSolarArrayApiPayload(raw.data);
  }
  if (raw.solarArray && typeof raw.solarArray === 'object') {
    return unwrapSolarArrayApiPayload(raw.solarArray);
  }
  return raw;
}

/** POST /solarArrays 응답 → { id, arrayId, name } */
export function normalizeSolarArrayApiResult(apiResult = {}) {
  const unwrapped = unwrapSolarArrayApiPayload(apiResult);
  return {
    id: resolveSolarArraySeqFromApiPayload(apiResult),
    arrayId: unwrapped?.arrayId ?? null,
    name: unwrapped?.name ?? null
  };
}

/** API 응답 래핑(data / content / solarString 등) 제거 */
export function unwrapSolarStringApiPayload(raw) {
  if (raw == null || typeof raw !== 'object') {
    return raw;
  }
  if (raw.id != null || raw.seq != null || raw.solarStringSeq != null) {
    return raw;
  }
  if (raw.content && typeof raw.content === 'object') {
    return unwrapSolarStringApiPayload(raw.content);
  }
  if (raw.data && typeof raw.data === 'object') {
    return unwrapSolarStringApiPayload(raw.data);
  }
  if (raw.solarString && typeof raw.solarString === 'object') {
    return unwrapSolarStringApiPayload(raw.solarString);
  }
  if (raw.result && typeof raw.result === 'object') {
    return unwrapSolarStringApiPayload(raw.result);
  }
  return raw;
}

/** POST/GET solarStrings 응답 본문·HAL에서 DB seq 추출 */
export function resolveSolarStringSeqFromApiPayload(raw) {
  if (raw == null) {
    return null;
  }
  const candidates = [raw, unwrapSolarStringApiPayload(raw)];
  for (const payload of candidates) {
    if (!payload || typeof payload !== 'object') {
      continue;
    }
    const fromId = readNumericId(payload.id ?? payload.seq ?? payload.solarStringSeq);
    if (fromId != null) {
      return fromId;
    }
    const href = payload._links?.self?.href;
    if (typeof href === 'string') {
      const fromHref = extractSolarStringSeqFromHalUri(href);
      if (fromHref != null) {
        return fromHref;
      }
    }
  }
  return null;
}

function resolveArrayInfo(flattened, arraySeq) {
  if (!flattened?.byArray || arraySeq == null) {
    return null;
  }
  return (
    flattened.byArray.get(arraySeq) ??
    flattened.byArray.get(String(arraySeq)) ??
    flattened.byArray.get(Number(arraySeq)) ??
    null
  );
}

function normalizeShapeGeog(dto = {}) {
  if (dto.shapeGeog) {
    return dto.shapeGeog;
  }
  if (Array.isArray(dto.shapeCoordinates) && dto.shapeCoordinates.length) {
    return { coordinates: [dto.shapeCoordinates] };
  }
  return undefined;
}

/** API 응답 + 요청 payload → SolarStringDto 형태 */
export function normalizeSolarStringApiResult(apiResult = {}, requestPayload = {}) {
  const unwrapped = unwrapSolarStringApiPayload(apiResult);
  const modulesFromApi = unwrapped?.solarModules;
  const modulesFromRequest = requestPayload?.solarModules || [];
  const solarModules = modulesFromApi?.length
    ? modulesFromApi
    : modulesFromRequest.map(m => ({
        moduleId: m.moduleId,
        shapeCoordinates: m.shapeCoordinates
      }));

  return {
    id: resolveSolarStringSeqFromApiPayload(apiResult),
    // 생성 요청에서 계산한 Array 내 번호·이름을 API 응답보다 우선 (백엔드 미반영 대비)
    stringId: requestPayload?.stringId ?? unwrapped?.stringId ?? null,
    name: requestPayload?.name ?? unwrapped?.name ?? null,
    rowNum: unwrapped?.rowNum ?? requestPayload?.rowNum ?? null,
    colNum: unwrapped?.colNum ?? requestPayload?.colNum ?? null,
    description: unwrapped?.description ?? requestPayload?.description ?? null,
    shapeGeog: normalizeShapeGeog(unwrapped) ?? normalizeShapeGeog(requestPayload),
    shapeCoordinates: unwrapped?.shapeCoordinates ?? requestPayload?.shapeCoordinates,
    solarModules: solarModules.map((m, index) => {
      const reqMod = requestPayload?.solarModules?.[index];
      return {
        id: m.id ?? m.seq ?? reqMod?.id ?? reqMod?.seq ?? null,
        moduleId: m.moduleId ?? reqMod?.moduleId,
        shapeGeog: m.shapeGeog ?? normalizeShapeGeog(m) ?? normalizeShapeGeog(reqMod),
        shapeCoordinates: m.shapeCoordinates ?? reqMod?.shapeCoordinates,
        moduleIndex: index
      };
    })
  };
}

function registerArrayInMap(byArray, arraySeq, arrayInfo) {
  byArray.set(arraySeq, arrayInfo);
  byArray.set(String(arraySeq), arrayInfo);
  const asNum = Number(arraySeq);
  if (Number.isFinite(asNum)) {
    byArray.set(asNum, arrayInfo);
  }
}

/** Array 추가 직후 flattened에 없을 때 트리·section 기준으로 최소 Array 레코드 보장 */
export function ensureArrayInfoInFlattened(
  flattened,
  arraySeq,
  { treeData, defaultSectionId } = {}
) {
  const existing = resolveArrayInfo(flattened, arraySeq);
  if (existing) {
    return { flattened, arrayInfo: existing, created: false };
  }

  const arrayNode = findArrayTreeItemBySeq(treeData, arraySeq);
  if (!arrayNode) {
    throw new Error(`Array not found in flattened data or tree: ${arraySeq}`);
  }

  const sectionId =
    defaultSectionId ??
    flattened?.bySection?.keys?.()?.next()?.()?.value ??
    null;
  const sectionInfo =
    sectionId != null ? flattened?.bySection?.get?.(sectionId) : null;
  const arrayIndex = (flattened?.flattened || []).filter((row) => row.level === 'array').length;

  const arrayInfo = {
    id: Number(arraySeq),
    arrayId: arrayNode.displayNo ?? arrayNode.arrayId ?? arrayNode.solarArrayId ?? null,
    seq: Number(arraySeq),
    displayNo: arrayNode.displayNo ?? arrayNode.arrayId ?? arrayNode.solarArrayId ?? null,
    name: arrayNode.name ?? `Array ${arraySeq}`,
    level: 'array',
    parentId: sectionId,
    sectionIndex: sectionInfo?.sectionIndex ?? 0,
    arrayIndex,
    children: [],
    solarStrings: []
  };

  const next = cloneFlattenedSiteDesign(flattened);
  next.flattened.push(arrayInfo);
  registerArrayInMap(next.byArray, arraySeq, arrayInfo);
  return { flattened: next, arrayInfo, created: true };
}

/** Array API 성공 후 flattened에 1건 append (트리는 ADD_ARRAY_TO_TREE 별도) */
export function appendArrayToFlattened(flattened, arrayInfo) {
  if (!arrayInfo || arrayInfo.id == null) {
    return flattened;
  }
  if (resolveArrayInfo(flattened, arrayInfo.id)) {
    return flattened;
  }
  const next = cloneFlattenedSiteDesign(flattened);
  next.flattened.push(arrayInfo);
  registerArrayInMap(next.byArray, arrayInfo.id, arrayInfo);
  return next;
}

/** API 삭제 후 flattened에서 Array 및 하위 String/Module 제거 */
export function removeArrayFromFlattened(flattened, arraySeq) {
  if (!flattened || arraySeq == null) {
    return flattened;
  }

  const next = cloneFlattenedSiteDesign(flattened);
  const seqKey = Number(arraySeq);
  const arrayInfo =
    resolveArrayInfo(next, arraySeq) ??
    resolveArrayInfo(next, seqKey) ??
    resolveArrayInfo(next, String(arraySeq));
  if (!arrayInfo) {
    return next;
  }

  const resolvedSeq = arrayInfo.id ?? arraySeq;
  const arrayKey = String(resolvedSeq);

  getDataByLevel(next, 'string')
    .filter(string => String(string.parentId) === arrayKey)
    .forEach(stringInfo => {
      const stringSeq = stringInfo.id;
      getDataByLevel(next, 'module')
        .filter(module => String(module.parentId) === String(stringSeq))
        .forEach(module => {
          const moduleIdx = next.flattened.findIndex(
            entry => String(entry.id) === String(module.id)
          );
          if (moduleIdx > -1) {
            next.flattened.splice(moduleIdx, 1);
          }
          next.byModule.delete(module.id);
        });

      const stringIdx = next.flattened.findIndex(entry => String(entry.id) === String(stringSeq));
      if (stringIdx > -1) {
        next.flattened.splice(stringIdx, 1);
      }
      next.byString.delete(stringSeq);
    });

  const arrayIdx = next.flattened.findIndex(
    entry => entry.level === 'array' && String(entry.id) === arrayKey
  );
  if (arrayIdx > -1) {
    next.flattened.splice(arrayIdx, 1);
  }
  next.byArray.delete(resolvedSeq);
  if (Number.isFinite(seqKey)) {
    next.byArray.delete(seqKey);
  }
  next.byArray.delete(String(arraySeq));

  return next;
}

function lookupModuleInfo(flattened, moduleSeq) {
  if (!flattened?.byModule || moduleSeq == null || moduleSeq === '') {
    return null;
  }
  return (
    flattened.byModule.get(moduleSeq) ??
    flattened.byModule.get(Number(moduleSeq)) ??
    flattened.byModule.get(String(moduleSeq)) ??
    null
  );
}

/** API 삭제 후 flattened에서 String 및 하위 Module 제거 */
export function removeStringFromFlattened(flattened, stringSeq) {
  if (!flattened || stringSeq == null) {
    return flattened;
  }

  const next = cloneFlattenedSiteDesign(flattened);
  const stringInfo = lookupStringInfo(next, stringSeq);
  if (!stringInfo) {
    return next;
  }

  const resolvedSeq = stringInfo.id ?? stringSeq;
  const parentArrayId = stringInfo.parentId;

  getDataByLevel(next, 'module')
    .filter(module => String(module.parentId) === String(resolvedSeq))
    .forEach(module => {
      const moduleIdx = next.flattened.findIndex(entry => String(entry.id) === String(module.id));
      if (moduleIdx > -1) {
        next.flattened.splice(moduleIdx, 1);
      }
      next.byModule.delete(module.id);
    });

  const stringIdx = next.flattened.findIndex(entry => String(entry.id) === String(resolvedSeq));
  if (stringIdx > -1) {
    next.flattened.splice(stringIdx, 1);
  }
  next.byString.delete(resolvedSeq);
  const seqKey = Number(stringSeq);
  if (Number.isFinite(seqKey)) {
    next.byString.delete(seqKey);
  }
  next.byString.delete(String(stringSeq));

  const parentArray = resolveArrayInfo(next, parentArrayId);
  detachStringFromArrayBuckets(parentArray, resolvedSeq);

  return next;
}

/** solarStrings에서 삭제된 String 레코드 제거 */
export function filterSolarStringsAfterStringDelete(solarStrings, stringInfo, stringSeq) {
  if (!stringInfo || !Array.isArray(solarStrings)) {
    return solarStrings || [];
  }

  const resolvedSeq = stringInfo.id ?? stringSeq;
  const arrayKey = stringInfo.parentId != null ? String(stringInfo.parentId) : null;

  return solarStrings.filter(s => {
    if (String(s.id) === String(resolvedSeq)) {
      return false;
    }
    if (arrayKey && String(s.stringId) === String(stringInfo.stringId)) {
      const sArray = String(s.solarArraySeq ?? s.solarArrayId ?? '');
      if (sArray === arrayKey) {
        return false;
      }
    }
    return true;
  });
}

/** API 삭제 후 flattened에서 Module 1건 제거 */
export function removeModuleFromFlattened(flattened, moduleSeq) {
  if (!flattened || moduleSeq == null || moduleSeq === '') {
    return flattened;
  }

  const next = cloneFlattenedSiteDesign(flattened);
  const moduleInfo = lookupModuleInfo(next, moduleSeq);
  if (!moduleInfo) {
    return next;
  }

  const resolvedSeq = moduleInfo.id ?? moduleSeq;
  const flatIdx = next.flattened.findIndex(entry => String(entry.id) === String(resolvedSeq));
  if (flatIdx > -1) {
    next.flattened.splice(flatIdx, 1);
  }
  next.byModule.delete(resolvedSeq);

  return next;
}

/** solarStrings nested solarModules에서 Module 1건 제거 */
export function removeModuleFromSolarStrings(solarStrings, moduleInfo, moduleSeq) {
  if (!moduleInfo || !Array.isArray(solarStrings)) {
    return;
  }

  const resolvedSeq = moduleInfo.id ?? moduleSeq;
  const parentStringId = moduleInfo.parentId;
  const solarString = solarStrings.find(
    s => String(s.id) === String(parentStringId) || String(s.stringId) === String(parentStringId)
  );
  if (!solarString?.solarModules) {
    return;
  }

  const moduleIdx = solarString.solarModules.findIndex(
    m => String(m.id) === String(resolvedSeq)
  );
  if (moduleIdx > -1) {
    solarString.solarModules.splice(moduleIdx, 1);
  }
}

/** API DTO → flattened Array 레코드 1건 */
export function buildArrayFlatRecordFromApi(
  apiDto,
  { flattened, sectionId, requestPayload = null } = {}
) {
  const arraySeq = apiDto?.id ?? apiDto?.seq;
  if (arraySeq == null) {
    throw new Error('Array seq missing from API result');
  }

  const resolvedSectionId =
    sectionId ??
    flattened?.bySection?.keys?.()?.next()?.()?.value ??
    null;
  const sectionInfo =
    resolvedSectionId != null ? flattened?.bySection?.get?.(resolvedSectionId) : null;
  const displayId = apiDto?.arrayId ?? requestPayload?.arrayId ?? null;
  const labelNum =
    (displayId != null ? String(displayId) : null) ??
    String(apiDto?.name ?? requestPayload?.name ?? '').match(/\d+/)?.[0] ??
    String(arraySeq);

  return {
    id: Number(arraySeq),
    arrayId: labelNum,
    seq: Number(arraySeq),
    displayNo: labelNum,
    name: apiDto?.name ?? requestPayload?.name ?? `Array ${labelNum}`,
    level: 'array',
    parentId: resolvedSectionId,
    sectionIndex: sectionInfo?.sectionIndex ?? 0,
    arrayIndex: (flattened?.flattened || []).filter(row => row.level === 'array').length,
    children: [],
    solarStrings: []
  };
}

/** flattened Array 레코드 → 트리 Array 노드 */
export function buildArrayTreeNodeFromFlat(arrayInfo) {
  const displayId = arrayInfo.arrayId ?? arrayInfo.displayNo ?? arrayInfo.id;
  const labelNum = String(displayId).match(/\d+/)?.[0] ?? String(displayId);
  return {
    name: arrayInfo.name || `Array ${labelNum}`,
    id: `array_${arrayInfo.id}`,
    arrayId: String(displayId),
    solarArraySeq: arrayInfo.id,
    solarArrayId: arrayInfo.id,
    type: 'array',
    children: []
  };
}

/**
 * API DTO → flattened String/Module 레코드
 * @returns {{ stringInfo: object, moduleInfos: object[], arrayInfo: object, sectionInfo: object|null }}
 */
export function buildStringFlatRecordsFromApi(apiDto, { arraySeq, flattened, requestPayload = null }) {
  const arrayInfo = resolveArrayInfo(flattened, arraySeq);
  if (!arrayInfo) {
    throw new Error(`Array not found in flattened data: ${arraySeq}`);
  }

  const sectionInfo = flattened?.bySection?.get?.(arrayInfo.parentId) ?? null;
  const existingStrings =
    arrayInfo.solarStrings?.length ??
    arrayInfo.children?.filter(c => c?.level === 'string').length ??
    0;
  const stringIndex = existingStrings;

  const shapeGeog = normalizeShapeGeog(apiDto);
  const stringDomain = stringFromApi({ ...apiDto, shapeGeog });
  const stringSeq = stringDomain.seq;
  if (stringSeq == null) {
    throw new Error('String seq missing from API result');
  }

  const stringInfo = {
    id: stringSeq,
    stringId: stringDomain.displayNo,
    seq: stringSeq,
    displayNo: stringDomain.displayNo,
    name: apiDto.name ?? `String ${stringDomain.displayNo ?? ''}`,
    rowNum: apiDto.rowNum,
    colNum: apiDto.colNum,
    description: apiDto.description,
    shapeGeog,
    level: 'string',
    parentId: arrayInfo.id ?? arraySeq,
    sectionIndex: arrayInfo.sectionIndex ?? sectionInfo?.sectionIndex,
    arrayIndex: arrayInfo.arrayIndex,
    stringIndex,
    children: [],
    solarModules: []
  };

  const moduleInfos = (apiDto.solarModules || []).map((mod, moduleIndex) => {
    const reqMod = requestPayload?.solarModules?.[moduleIndex];
    const md = moduleFromApi(mod);
    const modShape =
      mod.shapeGeog ??
      normalizeShapeGeog(mod) ??
      normalizeShapeGeog(reqMod);
    const moduleInfo = {
      id: md.seq,
      moduleId: md.displayNo,
      seq: md.seq,
      displayNo: md.displayNo,
      shapeGeog: modShape,
      centerCoord: mod.centerCoord,
      level: 'module',
      parentId: stringSeq,
      sectionIndex: stringInfo.sectionIndex,
      arrayIndex: stringInfo.arrayIndex,
      stringIndex,
      moduleIndex,
      children: []
    };
    stringInfo.children.push(moduleInfo);
    stringInfo.solarModules.push(moduleInfo);
    return moduleInfo;
  });

  return { stringInfo, moduleInfos, arrayInfo, sectionInfo };
}

/** flattened에 String + Module 1세트 추가 (immutable) */
export function appendStringWithModulesToFlattened(flattened, { stringInfo, moduleInfos }) {
  const next = cloneFlattenedSiteDesign(flattened);
  next.flattened.push(stringInfo);
  next.byString.set(stringInfo.id, stringInfo);

  moduleInfos.forEach(moduleInfo => {
    next.flattened.push(moduleInfo);
    if (moduleInfo.id != null) {
      next.byModule.set(moduleInfo.id, moduleInfo);
    }
  });

  const parentArray = resolveArrayInfo(next, stringInfo.parentId);
  if (parentArray) {
    if (!Array.isArray(parentArray.solarStrings)) {
      parentArray.solarStrings = [];
    }
    parentArray.solarStrings.push(stringInfo);
    if (!Array.isArray(parentArray.children)) {
      parentArray.children = [];
    }
    parentArray.children.push(stringInfo);
  }

  return next;
}

/** flattened String 레코드 → 트리 String 노드 (+ Module children) */
export function buildStringTreeNodeFromFlat(stringInfo, moduleInfos = []) {
  const stringDomain = stringFromFlatRecord(stringInfo);
  return {
    ...stringDomainToTreeFields(stringDomain),
    name: stringInfo.name || `String ${stringDomain.displayNo || ''}`,
    children: moduleInfos.map((moduleInfo, moduleIndex) => {
      const moduleDomain = moduleFromApi(moduleInfo);
      return {
        ...moduleDomainToTreeFields(moduleDomain),
        name: `Module ${moduleDomain.displayNo || moduleIndex + 1}`,
        children: []
      };
    })
  };
}

/** derivedTreeData에 String 노드 append (immutable) */
export function appendStringNodeToTree(treeData, arrayNodeId, stringNode) {
  return appendStringToTree(treeData, arrayNodeId, stringNode);
}

export function createStringCreatedPatch(apiResult, requestPayload, arraySeq) {
  return { apiResult, requestPayload, arraySeq };
}

export function createArrayCreatedPatch(apiResult, requestPayload, sectionId) {
  return { apiResult, requestPayload, sectionId };
}

export function createArrayDeletedPatch(arraySeq, arrayNodeId) {
  return { arraySeq, arrayNodeId };
}

export function createStringDeletedPatch(stringSeq, stringNodeId) {
  return { stringSeq, stringNodeId };
}

export function createModuleDeletedPatch(moduleSeq, moduleNodeId, parentStringNodeId) {
  return { moduleSeq, moduleNodeId, parentStringNodeId };
}

export function lookupStringInfo(flattened, stringSeq) {
  if (!flattened?.byString || stringSeq == null) {
    return null;
  }
  return (
    flattened.byString.get(stringSeq) ??
    flattened.byString.get(Number(stringSeq)) ??
    flattened.byString.get(String(stringSeq)) ??
    null
  );
}

function detachStringFromArrayBuckets(arrayInfo, stringSeq) {
  if (!arrayInfo) {
    return;
  }
  const key = String(stringSeq);
  if (Array.isArray(arrayInfo.solarStrings)) {
    const idx = arrayInfo.solarStrings.findIndex(s => String(s.id) === key);
    if (idx > -1) {
      arrayInfo.solarStrings.splice(idx, 1);
    }
  }
  if (Array.isArray(arrayInfo.children)) {
    const idx = arrayInfo.children.findIndex(
      child => child?.level === 'string' && String(child.id) === key
    );
    if (idx > -1) {
      arrayInfo.children.splice(idx, 1);
    }
  }
}

function attachStringToArrayBuckets(arrayInfo, stringInfo) {
  if (!arrayInfo || !stringInfo) {
    return;
  }
  if (!Array.isArray(arrayInfo.solarStrings)) {
    arrayInfo.solarStrings = [];
  }
  if (!arrayInfo.solarStrings.some(s => String(s.id) === String(stringInfo.id))) {
    arrayInfo.solarStrings.push(stringInfo);
  }
  if (!Array.isArray(arrayInfo.children)) {
    arrayInfo.children = [];
  }
  if (!arrayInfo.children.some(child => String(child.id) === String(stringInfo.id))) {
    arrayInfo.children.push(stringInfo);
  }
}

function applyStringDisplayUpdate(stringInfo, displayUpdate) {
  if (!stringInfo || !displayUpdate) {
    return;
  }

  const displayNo =
    displayUpdate.displayNo ?? displayUpdate.stringId ?? displayUpdate.solarStringId;
  if (displayNo != null && displayNo !== '') {
    const normalizedDisplayNo = String(displayNo);
    stringInfo.displayNo = normalizedDisplayNo;
    stringInfo.stringId = normalizedDisplayNo;
    stringInfo.solarStringId = normalizedDisplayNo;
  }
  if (displayUpdate.name != null && displayUpdate.name !== '') {
    stringInfo.name = displayUpdate.name;
  }
}

/**
 * String → 다른 Array 이동 후 flattened·solarStrings parent array 동기화
 * (max string display id 계산이 array 스코프 parentId/solarArraySeq에 의존)
 */
export function patchStringMoveToArrayInPlace(
  flattenedSiteDesign,
  solarStrings,
  stringSeq,
  targetArraySeq,
  displayUpdate = null
) {
  if (!flattenedSiteDesign || stringSeq == null || targetArraySeq == null) {
    return false;
  }

  const stringInfo = lookupStringInfo(flattenedSiteDesign, stringSeq);
  if (!stringInfo) {
    return false;
  }

  const resolvedSeq = stringInfo.id ?? stringSeq;
  const targetParentId = Number(targetArraySeq) || targetArraySeq;
  const oldParentId = stringInfo.parentId;
  const flatRow = flattenedSiteDesign.flattened?.find(
    item => item.level === 'string' && String(item.id) === String(resolvedSeq)
  );

  if (String(oldParentId) === String(targetParentId)) {
    applyStringDisplayUpdate(stringInfo, displayUpdate);
    applyStringDisplayUpdate(flatRow, displayUpdate);
    const record = solarStrings?.find(s => String(s.id) === String(resolvedSeq));
    applyStringDisplayUpdate(record, displayUpdate);
    return true;
  }

  const oldArray = resolveArrayInfo(flattenedSiteDesign, oldParentId);
  const newArray = resolveArrayInfo(flattenedSiteDesign, targetParentId);
  detachStringFromArrayBuckets(oldArray, resolvedSeq);

  stringInfo.parentId = targetParentId;
  stringInfo.solarArrayId = targetParentId;
  stringInfo.solarArraySeq = targetParentId;

  if (flatRow) {
    flatRow.parentId = targetParentId;
    flatRow.solarArrayId = targetParentId;
    flatRow.solarArraySeq = targetParentId;
  }

  attachStringToArrayBuckets(newArray, stringInfo);

  applyStringDisplayUpdate(stringInfo, displayUpdate);
  applyStringDisplayUpdate(flatRow, displayUpdate);

  if (Array.isArray(solarStrings)) {
    const record = solarStrings.find(s => String(s.id) === String(resolvedSeq));
    if (record) {
      record.solarArrayId = targetParentId;
      record.solarArraySeq = targetParentId;
      applyStringDisplayUpdate(record, displayUpdate);
    }
  }

  return true;
}
